import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, waHref } from "../lib/api";

const BTN = "border border-ink/20 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink hover:border-amber disabled:opacity-50";

export default function StudioAppointments() {
  const [appts, setAppts] = useState(null);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState("");

  const load = () => apiFetch("/admin/studio/appointments").then(setAppts);
  useEffect(() => { load(); }, []);

  const act = async (aid, action, extra = {}) => {
    try {
      await apiFetch(`/admin/studio/appointments/${aid}/${action}`, { method: "POST", body: JSON.stringify(extra) });
      setMsg(`Appointment ${action}d — client notified by email.`);
      load();
    } catch (e) {
      setMsg(e.message);
    }
  };

  if (!appts) return <p className="text-sm text-ink/50">Loading appointments…</p>;
  const requested = appts.filter((a) => a.status === "requested");
  const others = appts.filter((a) => a.status !== "requested");

  const Row = ({ a }) => (
    <li className="border-b border-ink/5 py-4 last:border-0" data-testid={`appt-row-${a.id}`}>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-semibold text-ink">{(a.requested_at || "").replace("T", " ")}</span>
        <span className="text-ink/60">{a.service}</span>
        <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${a.status === "approved" ? "border-emerald/40 bg-emerald/10 text-emerald" : a.status === "requested" ? "border-amber/40 bg-amber/10 text-amber" : "border-ink/15 text-ink/50"}`}>{a.status}</span>
      </div>
      {a.client && (
        <p className="mt-1 text-xs text-ink/60">
          <Link to={`/admin/studio/clients/${a.client.id}`} className="font-medium text-burgundy underline">{a.client.business_name}</Link> · {a.client.contact_name} ·{" "}
          <a href={waHref(a.client.phone, `Hi ${a.client.contact_name}, regarding your appointment request with IAGSB Digital Studio.`)} target="_blank" rel="noreferrer" className="font-semibold text-[#1faa55]">WhatsApp</a> · {a.client.email}
        </p>
      )}
      {a.status === "requested" && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`appt-actions-${a.id}`}>
          <button onClick={() => act(a.id, "approve", { confirmed_at: (form[a.id]?.confirmed_at || a.requested_at) })} className="border border-emerald/40 bg-emerald/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald" data-testid={`appt-approve-${a.id}`}>
            Approve requested slot
          </button>
          <input type="datetime-local" className="border border-ink/15 px-2 py-1 text-xs" value={form[a.id]?.confirmed_at || a.requested_at}
            onChange={(e) => setForm({ ...form, [a.id]: { ...(form[a.id] || {}), confirmed_at: e.target.value } })} data-testid={`appt-time-${a.id}`} />
          <button onClick={() => act(a.id, "propose", { proposed_at: form[a.id]?.confirmed_at || "", note: form[a.id]?.note || "" })} className={BTN} data-testid={`appt-propose-${a.id}`}>
            Propose alternative
          </button>
          <button onClick={() => act(a.id, "reject", { reason: form[a.id]?.note || "" })} className="border border-burgundy/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-burgundy" data-testid={`appt-reject-${a.id}`}>
            Decline
          </button>
          <input className="w-56 border border-ink/15 px-2 py-1 text-xs" placeholder="Note to client (optional)" value={form[a.id]?.note || ""}
            onChange={(e) => setForm({ ...form, [a.id]: { ...(form[a.id] || {}), note: e.target.value } })} data-testid={`appt-note-${a.id}`} />
        </div>
      )}
      {a.status === "proposed" && <p className="mt-1 text-xs text-ink/50">Proposed: {(a.proposed_at || "").replace("T", " ")} {a.note && `· ${a.note}`}</p>}
      {a.status === "approved" && <p className="mt-1 text-xs text-emerald">Confirmed: {(a.confirmed_at || "").replace("T", " ")}</p>}
    </li>
  );

  return (
    <div data-testid="studio-appointments-page">
      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">IAGSB Digital Studio</p>
      <h1 className="mt-2 font-serif text-3xl text-ink">Appointments</h1>
      <p className="mt-2 max-w-xl text-sm text-ink/60">Requests arrive by email and land here. Clients are only emailed a confirmation after you approve — all times Malaysia time (Asia/Kuala_Lumpur). Approving a taken slot is blocked automatically.</p>
      {msg && <p className="mt-4 border border-emerald/30 bg-emerald/5 px-4 py-2 text-sm text-emerald" data-testid="appt-flash">{msg}</p>}

      <div className="mt-8 border border-ink/10 bg-warmwhite p-6">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Awaiting your approval ({requested.length})</p>
        <ul>{requested.map((a) => <Row key={a.id} a={a} />)}</ul>
        {requested.length === 0 && <p className="mt-3 text-sm text-ink/40">Nothing waiting.</p>}
      </div>

      <div className="mt-6 border border-ink/10 bg-warmwhite p-6" data-testid="appt-history">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">History</p>
        <ul>{others.map((a) => <Row key={a.id} a={a} />)}</ul>
        {others.length === 0 && <p className="mt-3 text-sm text-ink/40">—</p>}
      </div>
    </div>
  );
}
